import React, { useState, useEffect, useMemo } from 'react'
import _ from 'lodash'
import qs from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, FormikProps, Form } from 'formik'
import * as yup from 'yup'
import { TFunction } from 'i18next'
import { Redirect } from 'react-router-dom'
import { ROUTES } from 'components/Root'
import { selectCountryFilter } from 'state/filter'
import { BrandState, clearBrand, getBrand } from 'state/brands'
import {
    selectTemplatesFilter,
    createCampaignByTemplate,
    selectTemplateData,
    getCampaignTemplates,
    getTemplateData,
    switchTemplate,
    OBJECTIVE,
} from 'state/campaignTemplates'
import { TemplateData } from 'services/api/campaignTemplates'
import { COUNTRY_ENUM, PLATFORM_CONTENT_TYPE_ENUM, PlatformContentTypeFilter } from 'services/api/constants'
import { getBrandsList, selectCampaignByTemplateData } from 'state'
import { showSnackBar } from 'services/toast'
import Text from 'particles/Text'
import { useHistoryPushWithForwardedParams, useNewTranslation, useWindowSize } from 'hooks'
import BrandSelection from './BrandSelection'
import CampaignRequirement from './CampaignRequirement'
import TemplateSelection from './TemplateSelection'
import SideNavigator, { StepOptions } from 'particles/SideNavigator'
import Modal from 'particles/Modal'
import Icon from 'components/Icon/Icon'
import { SmallScreenSideBar } from 'components/Campaign/Editor/EditTask/styles'
import { TemplateContainer } from '../style'
import IconButton from 'particles/Button/IconButton'
import CommentsCenter from 'components/Campaign/CommentsCenter'
import { useIdentifiersMap, useToggleCampaignEditorDrawer } from 'hooks/comment'
import TemplateActions from '../TemplateActions'
import { OnFormikValueChangeProps } from '../OnFormikValueChange'
import { ReviewPriorityFilter, ReviewTypeFilter } from './options'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import CampaignStatusLabel from '../CampaignStatusLabel'
import { TEMPLATE_TAB_ID } from '..'
import { selectTemplateFormErrors } from 'state/error/campaignTemplates'
import { ERROR_CODES } from 'state/error'

const getOptions = (t: TFunction, isAllCompleted: boolean): StepOptions[] => {
    return [
        {
            id: 0,
            title: t('campaign.template.step1.brandSelection.title'),
            subtext: t('campaign.template.step1.brandSelection.subText'),
            isCommented: false,
            isCompleted: isAllCompleted,
            isError: false,
            isDisabled: false,
        },
        {
            id: 1,
            title: t('campaign.template.step1.campaignRequirements.title'),
            subtext: t('campaign.template.step1.campaignRequirements.subtext'),
            isCommented: false,
            isCompleted: isAllCompleted,
            isError: false,
            isDisabled: !isAllCompleted,
        },
        {
            id: 2,
            title: t('campaign.template.step1.templateSelection.title'),
            subtext: t('campaign.template.step1.templateSelection.subtext'),
            isCommented: false,
            isCompleted: isAllCompleted,
            isError: false,
            isDisabled: !isAllCompleted,
        },
    ]
}

export interface TemplateFilterSchema {
    brandUuid: string | null
    ig_story_create_content_required: boolean | null
    get_product_required: boolean | null
    platform_content_types: PlatformContentTypeFilter[] | null
    review_type: ReviewTypeFilter | null
    review_priority: ReviewPriorityFilter | null
}

interface TemplateSelectorProps {
    setOpenInfoToast: React.Dispatch<React.SetStateAction<boolean>>
    setInfoToastMessage: React.Dispatch<React.SetStateAction<string>>
    setSelectTab: React.Dispatch<React.SetStateAction<TEMPLATE_TAB_ID>>
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({ setOpenInfoToast, setInfoToastMessage, setSelectTab }) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const historyPush = useHistoryPushWithForwardedParams()
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid, campaign: campaignUuid } = params
    const countryFilter = useSelector(selectCountryFilter)
    const templateFilter = useSelector(selectTemplatesFilter)
    const templateData = useSelector(selectTemplateData)
    const campaign = useSelector(selectCampaignByTemplateData)
    const isCampaignCreated = templateDataUuid != undefined
    const templateError = useSelector(selectTemplateFormErrors)
    const options = getOptions(t, isCampaignCreated)
    const [stepOptions, setStepOptions] = useState(options)
    const [selected, setSelected] = useState<number>(isCampaignCreated ? 2 : 0)
    const [currentTemplateUuid, setCurrentTemplateUuid] = useState<string>()
    const [showSwitchConfirmModal, setShowSwitchConfirmModal] = useState<boolean>(false)
    const [showSidebarTask, setShowSidebarTask] = useState(false)
    const brandsList = useSelector(getBrandsList)
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
    const selectedTabOption = stepOptions.find(option => option.id === selected)
    const selectedTabOptionIndex = stepOptions.findIndex(option => option.id === selected)
    const objective = templateData?.objective || templateFilter.objective
    const [showDrawer, setShowDrawer] = useState<boolean>(false)
    const searchParams = new URLSearchParams(location.search)
    const currentDrawerStatus = searchParams.get('drawer')
    const isCommentCenterOpened = currentDrawerStatus === 'comments'
    const updateToggleCampaignEditorDrawer = useToggleCampaignEditorDrawer()
    const { selectedIdentifier } = params
    const { campaignTemplateIdentifierMap, unresolvedTemplateFieldIdentifiers } = useIdentifiersMap()
    const windowSize = useWindowSize()
    const skipCampaignRequirement = objective === OBJECTIVE.appPromotion || objective === OBJECTIVE.content

    const templateDataToFormValues = (templateData?: TemplateData) => {
        if (templateData && templateData.platform_content_types) {
            let igStoryCreateContentRequired = null
            let reviewType = null
            let reviewPriority = null
            const platformContentTypes = templateData.platform_content_types.map(
                (platformContent): PlatformContentTypeFilter => {
                    const {
                        platform_content_type,
                        create_content_required,
                        approval_for_review_text_required,
                        image_for_review_text_required,
                    } = platformContent || {}
                    if (
                        platform_content_type === PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY &&
                        create_content_required
                    ) {
                        igStoryCreateContentRequired = true
                    } else if (
                        platform_content_type === PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY &&
                        create_content_required === false
                    ) {
                        igStoryCreateContentRequired = false
                    }

                    if (platform_content_type === PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW) {
                        if (typeof approval_for_review_text_required === 'boolean') {
                            reviewPriority = approval_for_review_text_required
                                ? 'review_with_approval'
                                : 'review_no_approval'
                        }

                        if (typeof image_for_review_text_required === 'boolean') {
                            reviewType = image_for_review_text_required ? 'review_text_image' : 'review_text_only'
                        }
                    }

                    return platform_content_type
                },
            )
            return {
                ...templateData,
                ig_story_create_content_required: igStoryCreateContentRequired,
                platform_content_types: platformContentTypes,
                review_type: reviewType,
                review_priority: reviewPriority,
            }
        } else {
            return undefined
        }
    }

    const templateFormValues = useMemo(() => templateDataToFormValues(templateData), [templateData])

    useEffect(() => {
        // Make sure the drawer is open if commentcenter is open
        if (isCommentCenterOpened) {
            setShowDrawer(true)
        }
    }, [])

    useEffect(() => {
        if (typeof selectedIdentifier === 'string' && campaignTemplateIdentifierMap[selectedIdentifier]) {
            const { stepKey } = campaignTemplateIdentifierMap[selectedIdentifier]
            const step =
                stepKey === 'brand_and_budget'
                    ? 0
                    : stepKey === 'campaign_requirements'
                      ? 1
                      : stepKey === 'template_selection'
                        ? 2
                        : null
            const stepNumber = Number(step)
            if (selectedIdentifier && stepNumber !== selected) {
                onSelectStep(stepNumber)
            }
        }
    }, [selectedIdentifier])

    useEffect(() => {
        // Make sure the drawer is open if commentcenter is open and is closed when comment center is not open
        setShowDrawer(isCommentCenterOpened)
    }, [updateToggleCampaignEditorDrawer])

    useEffect(() => {
        setStepOptions((prevState: StepOptions[]) => {
            const newOptions = prevState.map((option: StepOptions) => {
                const id =
                    option.id === 0
                        ? 'brand_and_budget'
                        : option.id === 1
                          ? 'campaign_requirements'
                          : option.id === 2
                            ? 'template_selection'
                            : null
                return {
                    ...option,
                    isCommented: id ? option.isCompleted && id in unresolvedTemplateFieldIdentifiers : false,
                    isError: id === 'brand_and_budget' ? !!templateError.brand : false,
                }
            })

            // Only update if there's a change
            const prevOptionsStr = JSON.stringify(prevState)
            const newOptionsStr = JSON.stringify(newOptions)

            if (prevOptionsStr !== newOptionsStr) {
                return newOptions
            }
            return prevState // Return previous state if no changes
        })
    }, [unresolvedTemplateFieldIdentifiers, templateError])

    useEffect(() => {
        if (templateData && templateData.brand.uuid) {
            // if user has selected some brand before. fetch brand detail to make brandList must have that brand item
            dispatch(getBrand({ uuid: templateData.brand.uuid }))
        }
        return () => {
            dispatch(clearBrand())
        }
    }, [dispatch, countryFilter, templateData])

    useEffect(() => {
        if (skipCampaignRequirement) {
            const newOption = stepOptions.filter(option => option.id !== 1)
            setStepOptions(newOption)
        }
    }, [templateFilter.objective])

    useEffect(() => {
        // if user refresh page after click draft campaign template from campaign list, we need to show current selected template
        if (!templateData && templateDataUuid && typeof templateDataUuid === 'string') {
            dispatch(getTemplateData({ templateDataUuid: templateDataUuid }))
        } else if (templateData && templateData?.template.uuid) {
            setCurrentTemplateUuid(templateData?.template.uuid)
        }
    }, [dispatch, templateDataUuid, templateData])

    useEffect(() => {
        if (
            templateFormValues &&
            _.isEmpty(templateFilter) &&
            typeof templateDataUuid === 'string' &&
            brandsList.length > 0 &&
            !selectedCountry
        ) {
            const templateBrand = brandsList.find((b: BrandState) => b.uuid === templateFormValues?.brand?.uuid)
            const localSelectedCountry = countryFilter || templateBrand?.country
            const shouldDispatch = localSelectedCountry && selectedCountry != localSelectedCountry
            // if user refresh page after click draft campaign template from campaign list, we need to show current selected template
            if (shouldDispatch) {
                setSelectedCountry(localSelectedCountry)
                dispatch(
                    getCampaignTemplates({
                        filter: {
                            country: localSelectedCountry as COUNTRY_ENUM,
                            limit: 100,
                        },
                        type: 'all',
                    }),
                )
                dispatch(
                    getCampaignTemplates({
                        filter: {
                            country: localSelectedCountry as COUNTRY_ENUM,
                            objective: templateFormValues.objective,
                            get_product_required: templateFormValues.get_product_required,
                            platform_content_types:
                                templateFormValues.platform_content_types &&
                                templateFormValues.platform_content_types.join(','),
                            // average_product_price: templateFormValues.average_product_price, // todo: enable this in milestone 3 flow
                        },
                        type: 'filter',
                    }),
                )
            }
        }
    }, [dispatch, templateDataUuid, templateFormValues, templateFilter, countryFilter, brandsList, selectedCountry])

    const initialValues = {
        country: selectedCountry,
        brandUuid: templateFormValues?.brand.uuid ?? null,
        templateUuid: templateFormValues?.template.uuid ?? null,
        objective: templateFormValues?.objective ?? null,
        ig_story_create_content_required: templateFormValues?.ig_story_create_content_required ?? null,
        get_product_required: templateFormValues?.get_product_required ?? null,
        platform_content_types: templateFormValues?.platform_content_types ?? null,
        review_type: templateFormValues?.review_type ?? null,
        review_priority: templateFormValues?.review_priority ?? null,
        // average_product_price: null, // todo: enable this in milestone 3 flow
    }
    const validationSchema = yup.object({
        brandUuid: yup
            .string()
            .nullable()
            .required(t('errorMessage.validation.selectBrand'))
            .test({
                message: () => t('errorMessage.validation.selectBrandSameCountry'),
                test: function (value) {
                    const selectedBrand = brandsList.find((b: BrandState) => b.uuid === value)
                    if (selectedCountry && selectedBrand && selectedCountry !== selectedBrand?.country) {
                        return false
                    }
                    return true
                },
            })
            .test({
                message: () => t('campaignEditor.setUpCampaign.enterBrandNameSubheader.errorMessage.missingBrandImage'),
                test: function (value) {
                    return ![ERROR_CODES.BRAND_IMAGE_NOT_FOUND, ERROR_CODES.BRAND_DESCRIPTION_REQUIRED].includes(
                        templateError.brand,
                    )
                },
            }),
        ig_story_create_content_required: yup
            .boolean()
            .nullable()
            .when('platform_content_types', {
                is: value => value && value.includes(PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY),
                then: yup.boolean().nullable().required(t('errorMessage.validation.emptySelection')),
                otherwise: yup.boolean().nullable(),
            }),
        review_type: yup
            .string()
            .nullable()
            .when('platform_content_types', {
                is: value => value && value.includes(PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW),
                then: yup.string().nullable().required(t('errorMessage.validation.emptySelection')),
                otherwise: yup.string().nullable(),
            }),
        review_priority: yup
            .string()
            .nullable()
            .when('platform_content_types', {
                is: value => value && value.includes(PLATFORM_CONTENT_TYPE_ENUM.WRITE_REVIEW),
                then: yup.string().nullable().required(t('errorMessage.validation.emptySelection')),
                otherwise: yup.string().nullable(),
            }),
        ...(!skipCampaignRequirement
            ? {
                  get_product_required: yup.boolean().nullable().required(t('errorMessage.validation.emptySelection')),
              }
            : {}),
        ...(!skipCampaignRequirement
            ? {
                  platform_content_types: yup.array().nullable().required(t('errorMessage.validation.emptySelection')),
              }
            : {}),
        // average_product_price: yup.number().required(''), // todo: enable this validation in milestone 3 flow
    })

    const completeToNextSection = () => {
        setStepComplete(selected)
        if (skipCampaignRequirement) {
            setSelected(2)
        } else if (selected + 1 < stepOptions.length) {
            setSelected(selected + 1)
        }
    }

    const backPreviousSection = () => {
        if (skipCampaignRequirement) {
            setSelected(0)
        } else if (selected - 1 >= 0) {
            setSelected(selected - 1)
        }
    }

    const setStepComplete = (completeStepIndex: number) => {
        const newStepOptions = stepOptions.map((option, index) => {
            if (index === completeStepIndex) {
                return { ...option, isCompleted: true }
            } else if (index === completeStepIndex + 1) {
                return { ...option, isDisabled: false }
            }
            return option
        })
        setStepOptions(newStepOptions)
    }

    const onCampaignCreateSuccessCallback = (templateDataUuid: string, campaignUuid: string) => {
        historyPush(`/campaign/template/templateDetail?templateData=${templateDataUuid}&campaign=${campaignUuid}`)
    }

    const onSwitchTemplateSuccessCallback = (templateDataUuid: string, campaignUuid: string) => {
        setSelectTab('templateDetail')
        historyPush(`/campaign/template/templateDetail?templateData=${templateDataUuid}&campaign=${campaignUuid}`)
    }

    const onCampaignCreateFailCallback = () => {
        showSnackBar({
            title: t('campaign.template.submitAndCreateCampaignDraft.errorMessage.generalError.subText'),
            type: 'error',
        })
    }
    const onSelectStep = (index: number, isDisabled?: boolean) => {
        const isTemplateValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isDisabled) {
            setInfoToastMessage(t('campaign.template.step1.tooltips.fillRequiredField'))
            setOpenInfoToast(true)
        } else {
            if (isTemplateValueChange === 'yes') {
                dispatch(
                    setConfirmPopup({
                        show: true,
                        onConfirm: () => {
                            setSelected(stepOptions[index].id as number)
                        },
                    }),
                )
            } else {
                setSelected(stepOptions[index].id as number)
            }
        }
    }

    // if user refresh page or not have draft campaign created, redirect them back to campaign list
    if (!templateFilter.objective && !templateDataUuid) {
        return <Redirect to={`${ROUTES.CampaignsDashboard}`} />
    }

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={() => {
                if (isCampaignCreated) {
                    setShowSwitchConfirmModal(true)
                } else {
                    dispatch(
                        createCampaignByTemplate({
                            templateFilter,
                            onSuccessCallback: onCampaignCreateSuccessCallback,
                            onFailCallback: onCampaignCreateFailCallback,
                        }),
                    )
                }
            }}
        >
            {(formikBag: FormikProps<TemplateFilterSchema>): JSX.Element => {
                const validateSpecificField: OnFormikValueChangeProps['validateSpecificField'] = async fieldNames => {
                    const errors: Awaited<ReturnType<OnFormikValueChangeProps['validateSpecificField']>> = {}
                    await Promise.all(
                        fieldNames.map(async fieldName => {
                            try {
                                await validationSchema.validateAt(fieldName, formikBag.values)
                            } catch (error) {
                                if (error instanceof yup.ValidationError) {
                                    errors[fieldName] = error.message
                                }
                            }
                        }),
                    )
                    formikBag.setErrors(errors)
                    return errors
                }
                return (
                    <Form style={{ height: '100%' }}>
                        <TemplateContainer showDrawer={showDrawer}>
                            <div className="top-block">
                                <div className="content-block">
                                    <Text scale="H3" color="primary">
                                        {t('campaign.template.step1.title')}
                                    </Text>
                                    {campaign && <CampaignStatusLabel status={campaign.status} />}
                                </div>

                                {templateData?.campaign.uuid && (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <IconButton
                                            form="outlined"
                                            size="s"
                                            color="neutral"
                                            icon="chatText"
                                            iconForm="regular"
                                            onClick={() => {
                                                setShowDrawer(!showDrawer)
                                                updateToggleCampaignEditorDrawer('comments')
                                            }}
                                            isActivated={isCommentCenterOpened}
                                            type="button"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="nav-bar-content">
                                <div className="small-sidebar-content">
                                    <div className="open-small-sidebar">
                                        <Text scale="bodyS500" color="primary">
                                            {t('campaignEditor.createTask.currentPageMobilePageTitle')}
                                        </Text>

                                        <div
                                            className="current-page-open-button"
                                            onClick={(): void => {
                                                setShowSidebarTask(true)
                                            }}
                                        >
                                            <Text scale="bodyS400" color="primary">
                                                {selectedTabOption?.title}
                                            </Text>
                                            <Icon.SelectorDownIcon />
                                        </div>
                                    </div>
                                </div>
                                <SideNavigator
                                    stepOptions={stepOptions}
                                    selected={selectedTabOptionIndex}
                                    onSelectStep={onSelectStep}
                                    isDisabledClickable={true}
                                    className="side-navigator"
                                    isExpanded={windowSize <= 1024 ? !showDrawer : true}
                                />
                                <div className="sidebar-divider" />
                                <div className="form-content">
                                    <div className="form-fields">
                                        {selected === 0 && (
                                            <BrandSelection
                                                completeToNextSection={completeToNextSection}
                                                validateSpecificField={validateSpecificField}
                                            />
                                        )}
                                        {selected === 1 && (
                                            <CampaignRequirement
                                                completeToNextSection={completeToNextSection}
                                                backPreviousSection={backPreviousSection}
                                                validateSpecificField={validateSpecificField}
                                                isReadOnly={isCampaignCreated}
                                            />
                                        )}
                                        {selected === 2 && (
                                            <TemplateSelection
                                                backPreviousSection={backPreviousSection}
                                                currentTemplateUuid={currentTemplateUuid}
                                            />
                                        )}
                                    </div>

                                    {typeof templateDataUuid === 'string' && (
                                        <TemplateActions templateUuid={templateDataUuid} />
                                    )}
                                </div>
                                {showDrawer && <div className="sidebar-divider" />}

                                <div className={`right-block ${showDrawer ? 'show' : ''}`}>
                                    <div className="right-block-inner-container">
                                        {isCommentCenterOpened && <CommentsCenter className="comments-center" />}
                                    </div>
                                </div>
                            </div>
                            {showSidebarTask && (
                                <SmallScreenSideBar
                                    className={`small-device-sidebar ${showSidebarTask && 'show-small-sidebar'}`}
                                    id="small-screen-sidebar-container"
                                    onClick={(e: any): void => {
                                        if (e.target.id === 'small-screen-sidebar-container') {
                                            setShowSidebarTask(false)
                                        } else {
                                            return
                                        }
                                    }}
                                >
                                    <SideNavigator
                                        stepOptions={stepOptions}
                                        selected={selectedTabOptionIndex}
                                        onSelectStep={onSelectStep}
                                        isDisabledClickable={true}
                                        className="side-navigator-content"
                                    />
                                </SmallScreenSideBar>
                            )}
                            {showSwitchConfirmModal && (
                                <Modal
                                    title={t('campaign.template.changeTemplateModal.title')}
                                    subtext={t('campaign.template.changeTemplateModal.subText')}
                                    size="s"
                                    icon="warningCircle"
                                    secondaryButtonProps={{ label: t('button.cancel') }}
                                    primaryButtonProps={{
                                        label: t('campaign.template.changeTemplateModal.primaryButton.text'),
                                    }}
                                    onConfirm={() => {
                                        if (
                                            typeof templateDataUuid === 'string' &&
                                            templateFilter.templateUuid &&
                                            typeof campaignUuid === 'string' &&
                                            campaignUuid
                                        ) {
                                            dispatch(
                                                switchTemplate({
                                                    templateDataUuid: templateDataUuid,
                                                    templateUuid: templateFilter.templateUuid,
                                                    campaignUuid: campaignUuid,
                                                    onSuccessCallback: onSwitchTemplateSuccessCallback,
                                                }),
                                            )
                                        }
                                    }}
                                    onClose={() => {
                                        setShowSwitchConfirmModal(false)
                                    }}
                                />
                            )}
                        </TemplateContainer>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default TemplateSelector
